import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ToolConnectionStatusCard } from './tool-connection-status';

/**
 * Connected status card for tool/provider credential configuration.
 */
export const ConnectedStatusCard: React.FC<{
  title: string;
  description: string;
  variant?: 'success' | 'default';
  isActive?: boolean;
  onDelete?: () => void;
}> = ({ title, description, isActive = false, onDelete }) => (
  <ToolConnectionStatusCard
    title={title}
    description={description}
    status="connected"
    isActive={isActive}
    onDelete={onDelete}
  />
);

/**
 * Inline type-to-confirm deletion panel shown below a credential card.
 */
export const InlineDeleteConfirmation: React.FC<{
  isPending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ isPending, onConfirm, onCancel }) => {
  const [confirmText, setConfirmText] = useState('');

  const handleConfirm = () => {
    if (confirmText === 'DELETE') {
      onConfirm();
    }
  };

  return (
    <div className="border border-border rounded-lg p-4">
      <p className="text-sm text-muted-foreground mb-3">
        This will permanently remove these credentials. Any tools using them
        will stop working until new credentials are provided.
      </p>
      <div className="flex items-end gap-3">
        <div className="flex-1 space-y-1.5">
          <Label htmlFor="delete-confirm-input" className="text-sm">
            Type <span className="font-mono font-semibold">DELETE</span> to
            confirm
          </Label>
          <Input
            id="delete-confirm-input"
            autoFocus
            placeholder="DELETE"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && confirmText === 'DELETE') {
                handleConfirm();
              }
            }}
          />
        </div>
        <Button variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={confirmText !== 'DELETE' || isPending}
          onClick={handleConfirm}
        >
          {isPending ? 'Deleting...' : 'Delete'}
        </Button>
      </div>
    </div>
  );
};
